import { useEffect, useState } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { LifeBuoy, Mail, ChevronDown, Download, RefreshCw, FileQuestion } from "lucide-react";

const FAQS = [
  {
    icon: Download,
    q: "How do I access my purchased plan?",
    a: "Right after payment, you'll be redirected to a Thank-You page with a personal download link. The same link is emailed to the address you used at checkout — usually within 60 seconds of payment. The link is yours forever (we never expire it) and supports unlimited re-downloads.",
  },
  {
    icon: Mail,
    q: "I didn't receive my download email — what now?",
    a: "Check your spam / promotions folder first (look for 'AIGL' as the sender). If you still can't find it, write to aiglchannel.info@gmail.com with the email address you used at checkout. We'll re-send the link manually within one business day. Your plan is safe and tied to your order — nothing is lost.",
  },
  {
    icon: RefreshCw,
    q: "Can I download my plan multiple times?",
    a: "Yes. Every download link works for life and supports re-downloads from any device. Some buyers download on their phone first, then again on their laptop. Both work.",
  },
  {
    icon: FileQuestion,
    q: "What format are the plans in? Can I edit them?",
    a: "All plans are delivered as professional, structured PDF documents. They're optimized for reading on phones, tablets, and laptops. While the PDFs are not directly editable, the content is laid out for you to reproduce in your own slide decks, lender pitch packs, or internal docs.",
  },
  {
    icon: LifeBuoy,
    q: "What is your refund policy?",
    a: "Because every plan is delivered instantly as a digital download, sales are generally final. However, if you genuinely believe a plan didn't match its description, write to aiglchannel.info@gmail.com within 7 days of purchase explaining what didn't meet expectations. We review every refund request individually and we'd rather refund an unhappy buyer than have you carry a bad experience.",
  },
];

export default function SupportPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Support · AIGL Channel — We've got you covered";
    return () => { document.title = prev; };
  }, []);
  const [open, setOpen] = useState(0);

  return (
    <div data-testid="support-page" className="min-h-screen bg-white">
      <Header />

      <section className="bg-[#0A192F] text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 pt-16 sm:pt-24 pb-14 sm:pb-20">
          <div className="font-accent text-[11px] tracking-[0.3em] text-[#C5A059] mb-5 flex items-center gap-3">
            <LifeBuoy className="w-4 h-4" /> APP STORE SUPPORT
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Need help? <span className="text-[#D4AF37] italic">We&apos;re here.</span>
          </h1>
          <p className="mt-6 text-white/70 max-w-2xl">
            Every email is answered by a real human within one business day — usually faster. No bots, no ticket queues, no hold music.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 py-12 sm:py-16">
        <div className="border-2 border-[#C5A059] bg-[#FBF8F1] p-7 sm:p-9 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="w-14 h-14 bg-[#0A192F] text-[#C5A059] flex items-center justify-center flex-shrink-0">
            <Mail className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="font-accent text-[10px] tracking-[0.3em] text-[#C5A059]">PRIMARY SUPPORT</div>
            <div className="font-display text-2xl sm:text-3xl text-[#0A192F] mt-1">
              <a href="mailto:aiglchannel.info@gmail.com" data-testid="support-primary-email" className="hover:text-[#C5A059]">
                aiglchannel.info@gmail.com
              </a>
            </div>
            <p className="mt-2 text-sm text-slate-600">
              Send us your order email + a quick description of what&apos;s going on. We typically reply within a few hours.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 pb-16 sm:pb-24">
        <div className="font-accent text-[10px] tracking-[0.3em] text-[#C5A059]">FREQUENTLY ASKED</div>
        <h2 className="mt-2 font-display text-3xl sm:text-4xl text-[#0A192F] mb-8">
          Quick answers to common questions
        </h2>

        <div className="space-y-3">
          {FAQS.map((f, idx) => {
            const isOpen = idx === open;
            return (
              <div
                key={idx}
                data-testid={`support-faq-${idx}`}
                className={`border ${isOpen ? "border-[#C5A059] bg-[#FBF8F1]" : "border-slate-200 bg-white"} transition`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : idx)}
                  className="w-full flex items-center gap-4 text-left px-5 py-4 sm:px-6 sm:py-5"
                  data-testid={`support-faq-toggle-${idx}`}
                >
                  <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center transition ${isOpen ? "bg-[#0A192F] text-[#C5A059]" : "bg-slate-100 text-[#0A192F]"}`}>
                    <f.icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 font-display text-base sm:text-lg text-[#0A192F]">{f.q}</div>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition ${isOpen ? "rotate-180 text-[#C5A059]" : ""}`} />
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 sm:px-6 sm:pb-6 pl-[76px] sm:pl-[88px] text-slate-700 leading-relaxed text-[15px]">
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
}
